import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { IVisibleLogic } from 'config'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { mapStepDetailDescription } from 'reducers/step.reducer'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button, InputVisibleLogic, Toggle } from 'stories/components'
import { confirm } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import {
  IOverviewStepTitleConfig,
  IOverviewStepTitleRule,
  overviewStepTitleKeys,
  TOverviewStepTitlesConfig,
} from './types'

const newRule = (): IOverviewStepTitleRule => ({
  title: '',
  visibleLogic: [],
})

const ensureStepConfig = (data: TOverviewStepTitlesConfig, key: string): IOverviewStepTitleConfig => {
  if (!data[key]) data[key] = { default: '', rules: [] }
  if (!Array.isArray(data[key].rules)) data[key].rules = []
  return data[key]
}

export const OverviewStepTitles = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<TOverviewStepTitlesConfig>({})
  const [changed, setChanged] = useState(false)

  useEffect(() => {
    initData()
  }, [])

  const initData = async () => {
    setLoading(true)
    try {
      const value = await getAdminConfig('overviewStepTitles')
      setData(value || {})
      setChanged(false)
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = async () => {
    setLoading(true)
    try {
      await setAdminConfig('overviewStepTitles', data)
      toast('Successfully submitted', { type: 'success' })
      setChanged(false)
    } finally {
      setLoading(false)
    }
  }

  const update = (mutator: (next: TOverviewStepTitlesConfig) => void) => {
    const next = cloneDeep(data)
    mutator(next)
    setData(next)
    setChanged(true)
  }

  const onChangeDefault = (key: string, value: string) => {
    update((next) => {
      ensureStepConfig(next, key).default = value
    })
  }

  const onChangeShowOnLoanOverview = (key: string, value: boolean) => {
    update((next) => {
      ensureStepConfig(next, key).show = value
    })
  }

  const onAddRule = (key: string) => {
    update((next) => {
      ensureStepConfig(next, key).rules.push(newRule())
    })
  }

  const onRemoveRule = async (key: string, ruleIndex: number) => {
    const ok = await confirm('Are you sure you want to remove this rule?')
    if (!ok) return
    update((next) => {
      ensureStepConfig(next, key).rules.splice(ruleIndex, 1)
    })
  }

  const onChangeRuleTitle = (key: string, ruleIndex: number, value: string) => {
    update((next) => {
      const cfg = ensureStepConfig(next, key)
      if (!cfg.rules[ruleIndex]) return
      cfg.rules[ruleIndex].title = value
    })
  }

  const onChangeRuleLogic = (key: string, ruleIndex: number, logic: IVisibleLogic[][]) => {
    update((next) => {
      const cfg = ensureStepConfig(next, key)
      if (!cfg.rules[ruleIndex]) return
      cfg.rules[ruleIndex].visibleLogic = logic
    })
  }

  const renderRule = (stepKey: string, rule: IOverviewStepTitleRule, ruleIndex: number) => {
    const ruleKey = `${stepKey}-rule-${ruleIndex}`
    return (
      <div className="border rounded-md p-3 mb-3 bg-white" key={ruleKey}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold">Rule {ruleIndex + 1}</p>
          <span className="btn-icon" onClick={() => onRemoveRule(stepKey, ruleIndex)}>
            <TrashIcon className="w-4 h-4 text-red-500" />
          </span>
        </div>

        <div className="mb-3">
          <RenderInput
            Key={`${ruleKey}-title`}
            input={{
              inputType: 'text',
              title: 'Title (used when conditions below match)',
              value: rule.title,
              required: true,
            }}
            onChange={(_k: string, value: string) => onChangeRuleTitle(stepKey, ruleIndex, value)}
          />
        </div>

        <InputVisibleLogic
          id={`${ruleKey}-logic`}
          title="Visible Conditions"
          value={rule.visibleLogic || []}
          options={{ hasNotVisible: true }}
          onChange={(value: IVisibleLogic[][]) => onChangeRuleLogic(stepKey, ruleIndex, value)}
        />
      </div>
    )
  }

  const renderStep = (stepKey: string, stepLabel: string) => {
    const cfg: IOverviewStepTitleConfig = data[stepKey] || { default: '', rules: [] }
    const rules = cfg.rules || []
    const fallback = mapStepDetailDescription[stepKey] || stepLabel
    return (
      <div className="mb-8" key={stepKey}>
        <RenderInput
          Key={`${stepKey}-section`}
          input={{
            inputType: 'section',
            title: stepLabel,
            additionalElements: (
              <div>
                <Toggle
                  id={`${stepKey}-toggle`}
                  title="Show on Loan Overview"
                  value={cfg.show || cfg.show === undefined}
                  onChange={(value: boolean) => onChangeShowOnLoanOverview(stepKey, value)}
                />
              </div>
            ),
          }}
          onChange={() => {}}
        />

        <div className="mt-4 pl-4">
          <div className="mb-4 max-w-xl">
            <RenderInput
              Key={`${stepKey}-default`}
              input={{
                inputType: 'text',
                title: `Default Title (falls back to "${fallback}" when empty)`,
                value: cfg.default || '',
              }}
              onChange={(_k: string, value: string) => onChangeDefault(stepKey, value)}
            />
          </div>

          <div className="bg-slate-50 p-3 rounded-md">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold">
                Conditional Titles
                <span className="text-xs text-gray-500 ml-2 font-normal">
                  The first rule whose conditions match wins. If no rule matches, the Default Title above is used.
                </span>
              </p>
              <span className="btn-icon" onClick={() => onAddRule(stepKey)}>
                <PlusIcon className="w-4 h-4" />
              </span>
            </div>

            {rules.length === 0 && (
              <p className="text-sm text-gray-500 italic">No conditional rules. Click the + above to add one.</p>
            )}

            {rules.map((rule, ruleIndex) => renderRule(stepKey, rule, ruleIndex))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative px-3 py-1.5 min-h-[100vh]">
      <LayoutLoading show={loading} />

      <div className="text-xl font-bold mb-4">Overview Step Title Overrides</div>
      <p className="text-sm text-gray-600 mb-6 max-w-3xl">
        Configure conditional titles for steps shown on the Loan Overview page. For each step, you can define one or
        more rules; each rule has its own title and a set of visibility conditions (the same logic as Loan Field
        Properties). The first rule whose conditions match the current loan wins. If no rule matches, the Default Title
        is used (falling back to the system default when empty).
      </p>

      {Object.keys(overviewStepTitleKeys).map((key) => renderStep(key, overviewStepTitleKeys[key]))}

      <div className="flex justify-center mt-4">
        <Button onClick={onSubmit} loading={loading} disabled={!changed}>
          Save Changes
        </Button>
      </div>
      <SaveChanges show={changed} label="Save Changes" onSave={onSubmit} />
    </div>
  )
}
